import {Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges} from '@angular/core';

/**
 * Setzt man auf ein bestehendes DIV. Während loading=true wird der Inhalt
 * versteckt und das DIV selbst wird zum grauen, pulsierenden Skeleton-Block.
 * Das Host-Element behält seine eigene Größe, Padding und Border-Radius.
 *
 * Beispiel:
 *   <div [appSkeletonLoading]="!dataLoaded" class="bg-gray-50 rounded-lg p-4">
 *     <span>{{ dynamicText }}</span>
 *   </div>
 */
@Directive({
  selector: '[appSkeletonLoading]',
  standalone: true
})
export class SkeletonLoadingDirective implements OnChanges {
  @Input() appSkeletonLoading = false;
  @Input() skeletonWidth = '';

  private isLoading = false;
  private hiddenChildren: HTMLElement[] = [];
  private appliedSkeletonClasses: string[] = [];

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appSkeletonLoading']) {
      const loading = !!changes['appSkeletonLoading'].currentValue;
      if (loading === this.isLoading) return;
      this.isLoading = loading;
      loading ? this.toSkeleton() : this.toContent();
    }
  }

  private toSkeleton(): void {
    const host = this.el.nativeElement;

    // Alle Element-Kinder verstecken
    this.hiddenChildren = Array.from(host.children) as HTMLElement[];
    this.hiddenChildren.forEach(c => this.renderer.setStyle(c, 'display', 'none'));

    // Text unsichtbar machen (für Text-Nodes wie {{ value }})
    this.renderer.setStyle(host, 'color', 'transparent');

    // Host-Element selbst zum Skeleton machen
    this.renderer.addClass(host, 'animate-pulse');
    this.renderer.setStyle(host, 'background-color', '#e5e7eb');
    this.renderer.addClass(host, 'rounded-full');
    this.renderer.setStyle(host, 'min-height', '20px');

    // Optionale Skeleton-Breite setzen
    this.appliedSkeletonClasses = this.skeletonWidth ? this.skeletonWidth.split(' ') : [];
    this.appliedSkeletonClasses.forEach(cls => this.renderer.addClass(host, cls));
  }

  private toContent(): void {
    const host = this.el.nativeElement;

    // Skeleton-Styles komplett entfernen
    this.renderer.removeClass(host, 'animate-pulse');
    this.renderer.removeStyle(host, 'background-color');
    this.renderer.removeStyle(host, 'color');
    this.renderer.removeClass(host, 'rounded-full');
    this.renderer.removeStyle(host, 'min-height');

    // Skeleton-Breite-Klassen entfernen
    this.appliedSkeletonClasses.forEach(cls => this.renderer.removeClass(host, cls));
    this.appliedSkeletonClasses = [];

    // Kinder wieder anzeigen
    this.hiddenChildren.forEach(c => this.renderer.removeStyle(c, 'display'));
    this.hiddenChildren = [];
  }
}

/**
 * Ersetzt den Textinhalt eines Elements durch eine graue Skeleton-Pille.
 * Das Element selbst bleibt sichtbar (z.B. Labels daneben bleiben).
 *
 * Verwendung:
 *   <span [appSkeletonPill]="loading" skeletonWidth="w-[120px]">{{ value }}</span>
 */
@Directive({
  selector: '[appSkeletonPill]',
  standalone: true
})
export class SkeletonPillDirective implements OnChanges {
  @Input() appSkeletonLoading = false;
  @Input() skeletonWidth = 'w-[120px]';

  private isLoading = false;
  private pill: HTMLElement | null = null;
  private originalDisplay = '';

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appSkeletonPill']) {
      const loading = !!changes['appSkeletonPill'].currentValue;
      if (loading === this.isLoading) return;
      this.isLoading = loading;
      loading ? this.toPill() : this.toContent();
    }
  }

  private toPill(): void {
    const host = this.el.nativeElement;

    // Original-Text verstecken
    this.originalDisplay = host.style.display;
    this.renderer.setStyle(host, 'display', 'none');

    // Pille erstellen und nach dem Element einfügen
    this.pill = this.renderer.createElement('div');
    this.renderer.addClass(this.pill, 'animate-pulse');
    this.renderer.addClass(this.pill, 'bg-gray-200');
    this.renderer.addClass(this.pill, 'h-[21px]');
    this.renderer.addClass(this.pill, 'rounded-[20px]');
    this.skeletonWidth.split(' ').forEach(cls => this.renderer.addClass(this.pill, cls));

    const parent = host.parentNode;
    if (parent) {
      this.renderer.insertBefore(parent, this.pill, host.nextSibling);
    }
  }

  private toContent(): void {
    const host = this.el.nativeElement;

    // Pille entfernen
    if (this.pill?.parentNode) {
      this.renderer.removeChild(this.pill.parentNode, this.pill);
      this.pill = null;
    }

    // Original wieder anzeigen
    if (this.originalDisplay) {
      this.renderer.setStyle(host, 'display', this.originalDisplay);
    } else {
      this.renderer.removeStyle(host, 'display');
    }
  }
}
